import React from 'react';

const ChatHistorySidebar = ({ 
  conversations, 
  currentConversationId, 
  onSelectConversation, 
  onNewChat,
  onDeleteConversation,
  onAnalyzeCode,
  isLoading,
  availableModels = [],
  currentModel = null,
  onModelChange
}) => {
  const [showModelSelector, setShowModelSelector] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [conversationToDelete, setConversationToDelete] = React.useState(null);
  
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const truncateTitle = (title, maxLength = 35) => {
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength - 3) + '...';
  };

  const handleDeleteClick = (e, conversation) => {
    e.stopPropagation();
    setConversationToDelete(conversation);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (conversationToDelete) {
      onDeleteConversation(conversationToDelete.id);
      setShowDeleteModal(false);
      setConversationToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setConversationToDelete(null);
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 border-l border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white space-y-2">
        <button
          onClick={onNewChat}
          className="w-full btn-primary py-1.5 px-3 text-xs font-medium flex items-center justify-center gap-1.5"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Chat
        </button>
        {onAnalyzeCode && (
          <button
            onClick={onAnalyzeCode}
            className="w-full btn-primary py-1.5 px-3 text-xs font-medium flex items-center justify-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 21h7a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v11m0 5l4.879-4.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242z" />
            </svg>
            Analyze Code
          </button>
        )}
      </div>

      {/* AI Model Selector */}
      {currentModel && availableModels.length > 0 && !currentModel.error && (
        <div className="p-3 border-b border-gray-200 bg-white">
          <div className="mb-2">
            <span className="text-xs font-semibold text-gray-700">AI Model</span>
          </div>
          <button
            onClick={() => setShowModelSelector(!showModelSelector)}
            className="w-full px-3 py-2 bg-blue-50 text-blue-900 rounded-md hover:bg-blue-100 transition-colors text-sm font-medium text-left flex items-center justify-between"
          >
            <span className="truncate">{currentModel.displayName || currentModel.modelName}</span>
            <svg 
              className={`w-4 h-4 transition-transform ${showModelSelector ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {/* Model Selector Dropdown */}
          {showModelSelector && (
            <div className="mt-2 space-y-1 max-h-64 overflow-y-auto bg-gray-100 p-2 rounded-lg border-2 border-blue-300 shadow-md">
              {availableModels.map((model) => (
                <button
                  key={model.modelName}
                  onClick={() => {
                    onModelChange(model.modelName);
                    setShowModelSelector(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    model.modelName === currentModel.modelName
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-blue-100 border border-gray-300 shadow-sm'
                  }`}
                >
                  <div className="font-medium truncate">{model.displayName || model.modelName}</div>
                  <div className="text-xs mt-1 opacity-80">
                    ${parseFloat(model.costInputTokens).toFixed(2)}/M in • ${parseFloat(model.costOutputTokens).toFixed(2)}/M out
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto p-2">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-sm text-gray-500">Loading...</div>
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-center px-4">
            <div className="text-sm text-gray-500">
              No conversations yet.<br />Start a new chat!
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            {conversations.map((conv) => (
              <div
                key={conv.id}
                className={`group relative rounded-lg p-3 cursor-pointer transition-colors ${
                  conv.id === currentConversationId
                    ? 'bg-blue-100 border border-blue-300'
                    : 'bg-white border border-gray-200 hover:bg-gray-50'
                }`}
                onClick={() => onSelectConversation(conv.id)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium truncate ${
                      conv.id === currentConversationId ? 'text-blue-900' : 'text-gray-900'
                    }`}>
                      {truncateTitle(conv.displayTitle || conv.title)}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-500">
                        {formatTimestamp(conv.updatedAt)}
                      </span>
                      {conv._count && conv._count.messages > 0 && (
                        <span className="text-xs text-gray-400">
                          • {conv._count.messages} msg{conv._count.messages !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Delete Button */}
                  <button
                    onClick={(e) => handleDeleteClick(e, conv)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-100 rounded text-red-600"
                    title="Delete conversation"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && conversationToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Conversation</h3>
            <p className="text-sm text-gray-700 mb-6">
              Are you sure you want to delete the conversation "{truncateTitle(conversationToDelete.displayTitle || conversationToDelete.title, 50)}"?
              This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancelDelete}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatHistorySidebar;
